import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  TextField,
  InputAdornment
} from '@mui/material';
import { Add, Search, Dashboard } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMasterDataManagement } from './MasterDataManagement/hooks/useMasterDataManagement';
import MasterDataTable from './MasterDataManagement/components/MasterDataTable';
import MasterDataFormDialog from './MasterDataManagement/components/MasterDataFormDialog';
import MasterDataDeleteDialog from './MasterDataManagement/components/MasterDataDeleteDialog';
import ConfigurationDetailsDialog from './MasterDataManagement/components/ConfigurationDetailsDialog';
import MasterDataDashboard from './MasterDataManagement/components/MasterDataDashboard';
import { masterDataTypes } from './MasterDataManagement/constants/masterDataConstants';

const MasterDataManagement = () => {
  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openTab !== undefined) {
      setTabValue(location.state.openTab + 1);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openTab, location.pathname, navigate]);

  const dataTypeIndex = tabValue >= 1 ? tabValue - 1 : 0;
  const {
    // State
    manufacturerSubTab,
    setManufacturerSubTab,
    data,
    open,
    setOpen,
    selectedItem,
    formData,
    setFormData,
    loading,
    assemblyTypes,
    manufacturers,
    models,
    ataChapters,
    manufacturerNames,
    manufacturerCategories,
    assemblyTypeConfigurations,
    partCategories,
    showArchived,
    setShowArchived,
    deleteDialogOpen,
    itemToDelete,
    deleteDependencies,
    deleteLoading,
    configDetailsDialogOpen,
    selectedConfiguration,
    canEdit,
    canDelete,
    // Computed
    currentType,
    effectiveType,
    // Handlers
    handleAdd,
    handleEdit,
    handleRestore,
    handleDelete,
    handleConfirmDelete,
    handleCloseDeleteDialog,
    handleSubmit,
    handleAddColumn,
    handleRemoveColumn,
    handleColumnChange,
    handleShowConfigurationDetails,
    handleCloseConfigurationDetails,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    sortedData,
    // Search
    searchQuery,
    setSearchQuery,
    filteredData
  } = useMasterDataManagement(dataTypeIndex);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Master Data Management
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(e, v) => {
            setTabValue(v);
            if (v >= 1 && masterDataTypes[v - 1].key !== 'manufacturers') {
              setManufacturerSubTab(0);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Dashboard />} iconPosition="start" label="Dashboard" />
          {masterDataTypes.map((type) => (
            <Tab key={type.key} label={type.label} />
          ))}
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Box role="tabpanel">
          <MasterDataDashboard />
        </Box>
      )}

      {tabValue >= 1 && (
        <>
      {/* Sub-tabs for Manufacturer Setup */}
      {currentType.key === 'manufacturers' && (
        <Paper sx={{ mb: 2 }}>
          <Tabs
            value={manufacturerSubTab}
            onChange={(e, v) => setManufacturerSubTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Names" />
            <Tab label="Aircraft Categories" />
            <Tab label="Primary Models" />
            <Tab label="Model Variants" />
          </Tabs>
        </Paper>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">{effectiveType.label}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                size="small"
              />
            }
            label="Show Archived"
            sx={{ ml: 1 }}
          />
        </Box>
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add {effectiveType.label.slice(0, -1)}
          </Button>
        )}
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search across all columns..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      <MasterDataTable
        data={filteredData}
        loading={loading}
        effectiveType={effectiveType}
        currentType={currentType}
        manufacturerSubTab={manufacturerSubTab}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={handleEdit}
        onRestore={handleRestore}
        onDelete={handleDelete}
        onShowConfigurationDetails={handleShowConfigurationDetails}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />

      <MasterDataFormDialog
        open={open}
        onClose={() => setOpen(false)}
        selectedItem={selectedItem}
        effectiveType={effectiveType}
        currentType={currentType}
        manufacturerSubTab={manufacturerSubTab}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        assemblyTypes={assemblyTypes}
        manufacturers={manufacturers}
        models={models}
        ataChapters={ataChapters}
    manufacturerNames={manufacturerNames}
    manufacturerCategories={manufacturerCategories}
    assemblyTypeConfigurations={assemblyTypeConfigurations}
    partCategories={partCategories}
    handleAddColumn={handleAddColumn}
        handleRemoveColumn={handleRemoveColumn}
        handleColumnChange={handleColumnChange}
      />

      <MasterDataDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        currentType={currentType}
        effectiveType={effectiveType}
        itemToDelete={itemToDelete}
        deleteDependencies={deleteDependencies}
        deleteLoading={deleteLoading}
        manufacturerSubTab={manufacturerSubTab}
      />

      <ConfigurationDetailsDialog
        open={configDetailsDialogOpen}
        onClose={handleCloseConfigurationDetails}
        configuration={selectedConfiguration}
      />
        </>
      )}
    </Box>
  );
};

export default MasterDataManagement;
